import { Visibility, Delete, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
    IconButton,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Radio,
    Tooltip,
    TableContainer,
    Paper,
    Box,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import colors from "colors/color";
import { Button as BootstrapButton } from 'bootstrap/dist/js/bootstrap.bundle.min';
import { OutlinedButton } from 'components/Buttons/Buttons';
import { useNavigate } from 'react-router-dom';
import SmartHireLoader from 'components/Loading/SmartHireLoader';
import ai_icon from "../../assets/img/ai_icon2.svg";

const ResumeSelectionList = ({ handleFileSelect: propHandleFileSelect }) => {
    const { t } = useTranslation();
    const apiUrl = process.env.REACT_APP_API_URL;
    const [uploadedFilesList, setUploadedFilesList] = useState({ resumes: null });
    const [selectedFile, setSelectedFile] = useState(null);
    const navigate = useNavigate();
    const [expandedRow, setExpandedRow] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);

    function getUserIdFromAccessToken() {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
            console.error("No access token found in local storage");
            return null;
        }
        try {
            const decodedToken = parseJwt(accessToken);
            return decodedToken.user_id;
        } catch (error) {
            console.error("Failed to decode access token", error);
            return null;
        }
    }

    function parseJwt(token) {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );
        return JSON.parse(jsonPayload);
    }

    const userId = getUserIdFromAccessToken();

    useEffect(() => {
        loadFiles();
    }, []);

    const navigateToResumeEditor = () => {
        navigate("/resume_editor");
    }

    const loadFiles = async () => {
        try {
            const response = await fetch(
                `${apiUrl}authentication/files-history-for-candidate/${userId}/`
            );
            if (!response.ok) {
                throw new Error(`An error occurred: ${response.statusText}`);
            }
            const json = await response.json();
            console.log('here is the ', json)
            setUploadedFilesList(json);
        } catch (error) {
            console.error("Error loading files:", error);
        }
    };

    const handleFileSelect = (fileKey, file) => {
        console.log("Selecting file:", { fileKey, file });
        setSelectedFile(fileKey);
        
        // Call the prop handler if provided
        if (propHandleFileSelect) {
            propHandleFileSelect(fileKey, file);
        }
    };

    const handleFileDelete = async (file_id) => {
        try {
            const response = await fetch(
                `${apiUrl}authentication/files-for-candidate-delete/${file_id}/`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`An error occurred: ${response.statusText}`);
            }

            const json = await response.json();
            console.log('File Deleted Successfully', json);
            loadFiles();
        } catch (error) {
            console.error("Error deleting file:", error);
        }
    };

    const handleShowFile = async (key, file) => {
        // Toggle expanded row
        setExpandedRow(expandedRow === key ? null : key);

        let fileURL;
        let resume_path;

        if (typeof file === "string") {
            resume_path = file;
        } else if (file && typeof file === "object") {
            resume_path = file.resume || file.path || file.name;
        } else {
            console.error("Invalid file type", file);
            return;
        }

        if (!resume_path) {
            console.log("Resume path is not available");
            return;
        }

        // Ensure the resume_path uses forward slashes
        const normalizedResumePath = resume_path.replace(/\\/g, "/");
        fileURL = `${apiUrl}media/${normalizedResumePath}`;

        try {
            const response = await fetch(`${apiUrl}ai/search_resume/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ resume_url: fileURL }),
            });

            const contentType = response.headers.get("content-type");

            if (response.ok) {
                if (contentType && contentType.includes("application/pdf")) {
                    // Create a blob from the PDF content
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    // Open the PDF in a new tab
                    window.open(url, "_blank");
                } else {
                    console.log("Unexpected response from server");
                }
            } else {
                if (contentType && contentType.includes("text/html")) {
                    // Handle HTML response (404 page)
                    const htmlContent = await response.text();
                    // Create a new window and write the HTML content to it
                    const newWindow = window.open();
                    newWindow.document.write(htmlContent);
                    newWindow.document.close();
                } else {
                    const data = await response.json();
                    console.log(data.message);
                }
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const openDeleteDialog = (file_id) => {
        setFileToDelete(file_id);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setFileToDelete(null);
    };

    const confirmDelete = () => {
        if (fileToDelete) {
            handleFileDelete(fileToDelete);
            closeDeleteDialog();
        }
    };

    if (!uploadedFilesList) {
        return <SmartHireLoader />;
    }

    return (
        <Grid item xs={12}>
            {Object.entries(uploadedFilesList).length === 0 ? (
                null
            ):(
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
            }}>
                <TableContainer
                    component={Paper}
                    sx={{
                        width: '80%',
                        maxWidth: '1200px',
                        borderRadius: 3,
                        boxShadow: 'none',
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0',
                        margin: '0 auto'
                    }}
                >
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow sx={{
                                background: '#fff',
                                '& .MuiTableCell-root': {
                                    color: `${colors.primary}`,
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                    textAlign: 'center',
                                    py: 3,
                                    borderBottom: '1.5px solid rgba(0,0,0,0.1)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',

                                }
                            }}>
                                {/* <TableCell sx={{ width: '60px' }}></TableCell> */}
                                <TableCell sx={{ width: '60px' }}>
                                    {t("applicationForm.select")}
                                </TableCell>
                                <TableCell>
                                    {t("applicationForm.fileName")}
                                </TableCell>
                                <TableCell>
                                    {t("applicationForm.date")}
                                </TableCell>
                                <TableCell>
                                    {t("applicationForm.actions")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(uploadedFilesList).length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        sx={{
                                            textAlign: 'center',
                                            py: 6,
                                            color: '#999',
                                            fontSize: '1.1rem',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        {t("No resume files found")}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                Object.entries(uploadedFilesList).map(
                                    ([key, file], index) =>
                                        file && (
                                            <React.Fragment key={key}>
                                                <TableRow
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                                                        transition: 'all 0.3s ease',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: '#f0f4ff',
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                                        },
                                                        '& .MuiTableCell-root': {
                                                            textAlign: 'center',
                                                            py: 2.5,
                                                            borderBottom: expandedRow === key ? 'none' : '1px solid #e8e8e8'
                                                        }
                                                    }}
                                                    onClick={() => handleFileSelect(key, file.resume_path || file)}
                                                >

                                                    <TableCell>
                                                        <Radio
                                                            checked={selectedFile === key}
                                                            onChange={() => handleFileSelect(key, file.resume_path || file)}
                                                            value={key}
                                                            sx={{
                                                                color: "#000000",
                                                                padding: "5px",
                                                                "&.Mui-checked": {
                                                                    color: "#0017E7",
                                                                },
                                                                "&:hover": {
                                                                    backgroundColor: "transparent",
                                                                },
                                                            }}
                                                            icon={
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        display: "block",
                                                                        width: 18,
                                                                        height: 18,
                                                                        border: "1px solid #000000",
                                                                        borderRadius: "50%",
                                                                    }}
                                                                />
                                                            }
                                                            checkedIcon={
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        display: "block",
                                                                        width: 18,
                                                                        height: 18,
                                                                        border: "1px solid #0017E7",
                                                                        borderRadius: "50%",
                                                                        backgroundColor: "#0017E7",
                                                                        position: "relative",
                                                                    }}
                                                                />
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        color: colors.primary,
                                                        fontWeight: 600,
                                                        fontSize: '1rem',
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {file.resume_path ? file.resume_path.split('/').pop() : `Resume ${key}`}
                                                    </TableCell>

                                                    <TableCell sx={{
                                                        color: '#555',
                                                        fontWeight: 500,
                                                        fontSize: '0.95rem'
                                                    }}>
                                                        {file.date
                                                            ? new Date(file.date).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            })
                                                            : new Date().toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            })}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            gap: 1
                                                        }}>
                                                            <Tooltip title="View File" arrow>
                                                                <IconButton
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleShowFile(key, file.resume_path || file);
                                                                    }}
                                                                    sx={{
                                                                        color: '#4caf50',
                                                                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                                                        borderRadius: '50%',
                                                                        padding: '8px',
                                                                        transition: 'all 0.3s ease',
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                                                            transform: 'scale(1.1)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Visibility fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete File" arrow>
                                                                <IconButton
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openDeleteDialog(file.id);
                                                                    }}
                                                                    sx={{
                                                                        color: '#f44336',
                                                                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                                                        borderRadius: '50%',
                                                                        padding: '8px',
                                                                        transition: 'all 0.3s ease',
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                                                            transform: 'scale(1.1)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        )
                                )
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
           )}
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    {t("applicationForm.confirmDeleteTitle") || "Confirm Delete"}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {t("applicationForm.confirmDeleteMessage") || "Are you sure you want to delete this file? This action cannot be undone."}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} color="primary">
                        {t("applicationForm.cancel") || "Cancel"}
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        {t("applicationForm.delete") || "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid> 
        
    );
};

export default ResumeSelectionList;